import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { PomodoroPhase } from '@/hooks/usePomodoro';
import { usePomodoroContext } from '@/contexts/PomodoroContext';
import { useProjects } from '@/hooks/useProject';
import { useTranslation } from 'react-i18next';

const LONG_PRESS_MS = 500;

const PHASE_COLORS: Record<PomodoroPhase, { ring: string; bg: string; text: string }> = {
  work: { ring: '#10b981', bg: 'bg-emerald-600', text: 'text-emerald-600 dark:text-emerald-400' },
  shortBreak: { ring: '#3b82f6', bg: 'bg-blue-600', text: 'text-blue-600 dark:text-blue-400' },
  longBreak: { ring: '#8b5cf6', bg: 'bg-violet-600', text: 'text-violet-600 dark:text-violet-400' },
};

const PHASE_LABELS: Record<PomodoroPhase, string> = {
  work: 'Work',
  shortBreak: 'Break',
  longBreak: 'Long Break',
};

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const PomodoroWidget: React.FC = () => {
  const {
    phase,
    running,
    remainingSeconds,
    totalDuration,
    sessionsCompleted,
    settings,
    selectedProject,
    setSelectedProject,
    start,
    pause,
    reset,
    skip,
    setPhase,
    toggleSound,
    requestNotificationPermission,
  } = usePomodoroContext();

  const { t } = useTranslation('pomodoro');
  const { data: projectsData } = useProjects();
  const activeProjects = useMemo(() =>
    (projectsData?.projects || []).filter(p => p.status === 'active' || p.status === 'planning'),
    [projectsData]
  );

  const [expanded, setExpanded] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);

  // --- Draggable via long press ---
  const [draggable, setDraggable] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const didDrag = useRef(false);

  const clampPosition = useCallback((x: number, y: number) => {
    const el = widgetRef.current;
    if (!el) return { x, y };
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    return {
      x: Math.max(0, Math.min(window.innerWidth - w, x)),
      y: Math.max(0, Math.min(window.innerHeight - h, y)),
    };
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // Only primary button
    if (e.button !== 0) return;
    didDrag.current = false;

    const startX = e.clientX;
    const startY = e.clientY;

    longPressTimer.current = setTimeout(() => {
      setDraggable(true);
      isDragging.current = true;

      // Compute offset from pointer to widget top-left
      const el = widgetRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        dragOffset.current = { x: startX - rect.left, y: startY - rect.top };
        // Initialize position from current rect if not yet set
        if (!position) {
          setPosition({ x: rect.left, y: rect.top });
        }
      }
      // Capture pointer for smooth dragging
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    }, LONG_PRESS_MS);
  }, [position]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    e.preventDefault();
    didDrag.current = true;
    const newPos = clampPosition(
      e.clientX - dragOffset.current.x,
      e.clientY - dragOffset.current.y,
    );
    setPosition(newPos);
  }, [clampPosition]);

  const handlePointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    isDragging.current = false;
  }, []);

  // Cancel long press if pointer moves before threshold
  const handlePointerCancel = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    isDragging.current = false;
  }, []);

  // Exit draggable mode on outside click
  useEffect(() => {
    if (!draggable) return;
    const handler = (e: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(e.target as Node)) {
        setDraggable(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [draggable]);

  // Recalculate position on window resize
  useEffect(() => {
    if (!position) return;
    const handler = () => setPosition(prev => prev ? clampPosition(prev.x, prev.y) : null);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [position, clampPosition]);

  // Close on outside click
  useEffect(() => {
    if (!expanded) return;
    const handleClick = (e: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [expanded]);

  // Positioning style: custom position or default bottom-right
  const posStyle: React.CSSProperties = position
    ? { position: 'fixed', left: position.x, top: position.y, bottom: 'auto', right: 'auto', zIndex: 40 }
    : {};

  const progress = totalDuration > 0 ? (totalDuration - remainingSeconds) / totalDuration : 0;
  const colors = PHASE_COLORS[phase];

  // SVG ring parameters
  const radius = expanded ? 52 : 22;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  if (!expanded) {
    // Collapsed: small circle
    return (
      <div
        className={`fixed ${position ? '' : 'bottom-4 right-4'} z-40 select-none touch-none`}
        ref={widgetRef}
        style={posStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <button
          onClick={() => { if (!didDrag.current) setExpanded(true); }}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
            draggable
              ? 'bg-yellow-50 dark:bg-yellow-900/30 border-2 border-dashed border-yellow-400 shadow-[0_0_12px_rgba(234,179,8,0.4)] scale-110 cursor-grab active:cursor-grabbing'
              : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-xl'
          } ${running && !draggable ? 'animate-pulse' : ''}`}
          title={draggable ? 'Drag to move • Click elsewhere to lock' : `${PHASE_LABELS[phase]} - ${formatTime(remainingSeconds)}`}
        >
          <svg width="48" height="48" className="absolute">
            <circle
              cx="24"
              cy="24"
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              className="text-gray-200 dark:text-gray-700"
            />
            <circle
              cx="24"
              cy="24"
              r={radius}
              fill="none"
              stroke={colors.ring}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-300"
              style={{ transform: 'rotate(-90deg)', transformOrigin: '24px 24px' }}
            />
          </svg>
          <span className={`text-[10px] font-bold ${colors.text} z-10`}>
            {formatTime(remainingSeconds)}
          </span>
        </button>
        {/* Draggable indicator */}
        {draggable && (
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap shadow animate-bounce">
            Drag to move
          </div>
        )}
        {selectedProject && !draggable && (
          <div className="absolute -top-1 -left-1 bg-gray-700 text-white text-[7px] px-1 rounded leading-tight max-w-[56px] truncate">
            {selectedProject.project_code}
          </div>
        )}
      </div>
    );
  }

  // Expanded card
  return (
    <div
      className={`fixed ${position ? '' : 'bottom-4 right-4'} z-40 select-none`}
      ref={widgetRef}
      style={posStyle}
    >
      <div className={`w-60 rounded-xl shadow-2xl overflow-hidden transition-all duration-200 ${
        draggable
          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-2 border-dashed border-yellow-400 shadow-[0_0_16px_rgba(234,179,8,0.35)]'
          : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
      }`}>
        {/* Header — drag handle when in draggable mode */}
        <div
          className={`flex items-center justify-between px-3 py-2 border-b transition-colors ${
            draggable
              ? 'border-yellow-300 dark:border-yellow-700 bg-yellow-100 dark:bg-yellow-900/30 cursor-grab active:cursor-grabbing'
              : 'border-gray-100 dark:border-gray-700'
          }`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          style={{ touchAction: 'none' }}
        >
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
            {draggable && (
              <svg className="w-3 h-3 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
              </svg>
            )}
            Pomodoro
          </span>
          <div className="flex items-center gap-1">
            {/* Sound toggle */}
            <button
              onClick={toggleSound}
              className={`p-1 transition-colors ${
                settings.soundEnabled
                  ? 'text-emerald-500 hover:text-emerald-600'
                  : 'text-gray-300 dark:text-gray-600 hover:text-gray-400'
              }`}
              title={settings.soundEnabled ? 'Sound on' : 'Sound off'}
            >
              {settings.soundEnabled ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M17.95 6.05a8 8 0 010 11.9M6.5 8.788l4.95-2.829A.5.5 0 0112 6.38v11.24a.5.5 0 01-.55.42L6.5 15.212H4a1 1 0 01-1-1v-4.424a1 1 0 011-1h2.5z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              )}
            </button>
            {/* Close */}
            <button
              onClick={() => setExpanded(false)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Project selector */}
        <div className="px-3 pt-2">
          <select
            value={selectedProject?.id || ''}
            onChange={(e) => {
              const proj = activeProjects.find(p => p.id === e.target.value);
              setSelectedProject(proj ? { id: proj.id, project_name: proj.project_name, project_code: proj.project_code } : null);
            }}
            className="w-full text-[11px] px-2 py-1 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            <option value="">{t('noProject')}</option>
            {activeProjects.map(p => (
              <option key={p.id} value={p.id}>{p.project_code} {p.project_name}</option>
            ))}
          </select>
        </div>

        {/* Timer ring */}
        <div className="flex flex-col items-center py-4">
          <div className="relative w-[120px] h-[120px] flex items-center justify-center">
            <svg width="120" height="120">
              <circle
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth="5"
                className="text-gray-200 dark:text-gray-700"
              />
              <circle
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={colors.ring}
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className="transition-all duration-300"
                style={{ transform: 'rotate(-90deg)', transformOrigin: '60px 60px' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-2xl font-bold ${colors.text}`}>
                {formatTime(remainingSeconds)}
              </span>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">
                {PHASE_LABELS[phase]}
              </span>
            </div>
          </div>
        </div>

        {/* Phase pills */}
        <div className="flex justify-center gap-1.5 px-3 pb-3">
          {(['work', 'shortBreak', 'longBreak'] as PomodoroPhase[]).map((p) => (
            <button
              key={p}
              onClick={() => setPhase(p)}
              className={`px-2 py-0.5 text-[10px] rounded-full font-medium transition-colors ${
                phase === p
                  ? `${PHASE_COLORS[p].bg} text-white`
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {PHASE_LABELS[p]}
            </button>
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-2 px-3 pb-3">
          <button
            onClick={reset}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Reset"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          <button
            onClick={() => {
              if (running) {
                pause();
              } else {
                requestNotificationPermission();
                start();
              }
            }}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-colors ${colors.bg} hover:opacity-90`}
          >
            {running ? 'Pause' : 'Start'}
          </button>

          <button
            onClick={skip}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Skip"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Session count */}
        <div className="px-3 pb-2 text-center">
          <span className="text-[10px] text-gray-400 dark:text-gray-500">
            Sessions: {sessionsCompleted}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PomodoroWidget;
